"use client";

import { useEffect } from "react";

import { triggerHaptic } from "./haptic";

/**
 * Selector for elements that should fire a `light` haptic on pointer down.
 * Extends the legacy `button, [role="button"], nav a` set with tab/switch/
 * menuitem roles so swipe-able tab triggers + setting toggles + dropdown
 * items also get the feedback.
 *
 * Notable choices:
 * - `[role="tab"]` covers SwipeableTabsContent triggers (always button under
 *   the hood, but the role is the more reliable selector across components).
 * - `[role="switch"]` covers the iOS-style toggles in account settings.
 * - `[role="menuitem"]` covers dropdown menus (header user menu, theme
 *   switcher, locale switcher).
 * - `<a>` is restricted to `<nav>` containers to avoid firing on every
 *   in-text link (would feel buzzy in dense article copy).
 */
const HAPTIC_SELECTOR =
  'button, [role="button"], [role="tab"], [role="switch"], [role="menuitem"], nav a';

/**
 * Global haptic provider. Listens once on `document` for `pointerdown` and
 * fires a light vibration when the pointer lands on any matched interactive
 * element (see HAPTIC_SELECTOR). Mount once per app root — no per-component
 * wiring needed.
 *
 * iOS Safari does not implement the Vibration API; this becomes a no-op
 * there. Android Chrome / Firefox / Edge respect it.
 */
export function HapticProvider() {
  useEffect(() => {
    if (typeof navigator === "undefined" || !("vibrate" in navigator)) return;

    function onPointerDown(e: PointerEvent) {
      const target = e.target as Element | null;
      if (!target?.closest) return;

      const match = target.closest(HAPTIC_SELECTOR) as HTMLElement | null;
      if (!match) return;

      // Skip disabled / aria-disabled buttons so a user attempting to tap a
      // greyed-out CTA does not get a misleading "tap registered" buzz.
      if (match.hasAttribute("disabled") || match.getAttribute("aria-disabled") === "true") {
        return;
      }

      triggerHaptic("light");
    }

    document.addEventListener("pointerdown", onPointerDown, { passive: true });
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return null;
}
